"use client";

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import useIsMobile from '@/app/hooks/useIsMobile';
import useTranslations from '@/app/hooks/useTranslation';
import { useNavbar } from '@/app/contexts/NavbarContext';
import ContactUs from '../../[lang]/home-page/sections/contactUs/contactUs';
import { Typography } from '@mui/material';
import { getDictionary, getField } from '@/app/utils/translation/translation';

// Client component wrapper for ContactUs
export default function ContactUsClient() {
    const isMobile = useIsMobile();
    const { language, translations } = useTranslations();
    const { contactusRef } = useNavbar();
    const [mounted, setMounted] = useState(false);
    const pathname = usePathname();
    const isArabic = language === 'arabic';

    // Prevent hydration mismatch
    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) {
        return null;
    }

    // Normalize pathname to handle language codes (e.g., /ar/online-ordering or /online-ordering)
    // Remove leading language code if present and trailing slashes
    const cleanPathname = pathname ? pathname.replace(/^\/(ar|fr|en)\//, '/').replace(/\/$/, '') : '';
    const pathToCheck = cleanPathname || pathname?.replace(/\/$/, '') || '';

    // Pages that should NOT show contact form
    const excludedPages = ['/terms', '/privacy-policy', '/thankyou'];
    const shouldExclude = excludedPages.some(page => pathToCheck === page);
    
    if (shouldExclude) {
        return null;
    }

    // Determine text, showTitle, and sideImage based on current page
    let text: React.ReactNode | undefined = undefined;
    let showTitle = true;
    let sideImage: string | undefined = undefined;
    
    const dictionary = getDictionary(translations, 'ordering', language);
    const dictionaryCrm = getDictionary(translations, 'crm-marketing', language);
    const dictionaryBranded = getDictionary(translations, 'branded-apps', language);
    const dictionaryQr = getDictionary(translations, 'qr-ordering', language);
    
    // Check if pathname exactly equals the route (works for both /online-ordering and /ar/online-ordering)
    // Using exact match instead of includes to avoid false matches on blog pages or other routes
    if (pathToCheck === '/online-ordering') {
        text = (
            <Typography
                paragraph
                component="h4"
                variant="h4"
                fontWeight={"bold"}
                textAlign={isArabic ? "right" : "left"}
                fontFamily="Century Gothic"
                fontSize={isMobile ? '0.9rem' : "1rem"}
                sx={{ color: "var(--text-primary)" }}
                marginTop={isMobile ? "1rem" : "4rem"}
            >
                {getField(dictionary, 'text45', language)}
            </Typography>
        );
        showTitle = true;
    } else if (pathToCheck === '/crm-marketing') {
        const crmText = getField(dictionaryCrm, 'contact-desc', language) || getField(dictionaryCrm, 'text16', language) || getField(dictionaryCrm, 'title', language);
        if (crmText) {
            text = (
                <Typography 
                    paragraph
                    component="h4"
                    variant="h4"
                    fontWeight={"bold"}
                    textAlign={isArabic ? "right" : "left"}
                    fontFamily="Century Gothic"
                    fontSize={isMobile ? '0.9rem' : "1rem"}
                    marginTop={isMobile ? "1rem" : "4rem"}
                    sx={{
                        fontSize: isMobile ? '0.9rem' : "1rem",
                        color: "var(--text-primary)",
                        textAlign: isArabic ? "right" : "left",
                        fontWeight: "bold",
                        fontFamily: "Century Gothic",
                        marginTop: isMobile ? "1rem" : "4rem"
                    }}
                >
                    {crmText}
                </Typography>
            );
        }
        showTitle = true;
    } else if (pathToCheck === '/branded-apps') {
        const brandedText = getField(dictionaryBranded, 'contact-us', language) || getField(dictionaryBranded, 'text16', language) || getField(dictionaryBranded, 'title', language);
        if (brandedText) {
            text = (
                <Typography 
                    paragraph
                    component="h4"
                    variant="h4"
                    fontWeight={"bold"}
                    textAlign={isArabic ? "right" : "left"}
                    fontFamily="Century Gothic"
                    fontSize={isMobile ? '0.9rem' : "1rem"}
                    marginTop={isMobile ? "1rem" : "4rem"}
                    sx={{
                        fontSize: isMobile ? '0.9rem' : "1rem",
                        color: "var(--text-primary)",
                        textAlign: isArabic ? "right" : "left",
                        fontWeight: "bold",
                        fontFamily: "Century Gothic",
                        marginTop: isMobile ? "1rem" : "4rem"
                    }}
                >
                    {brandedText}
                </Typography>
            );
        }
        showTitle = true;
    } else if (pathToCheck === '/qr-ordering') {
        const qrText = getField(dictionaryQr, 'contact-desc', language) || getField(dictionaryQr, 'contact-us', language) || getField(dictionaryQr, 'text16', language) || getField(dictionaryQr, 'title', language);
        if (qrText) {
            text = (
                <Typography 
                    paragraph
                    component="h4"
                    variant="h4"
                    fontWeight={"bold"}
                    textAlign={isArabic ? "right" : "left"}
                    fontFamily="Century Gothic"
                    fontSize={isMobile ? '0.9rem' : "1rem"}
                    marginTop={isMobile ? "1rem" : "4rem"}
                    sx={{
                        fontSize: isMobile ? '0.9rem' : "1rem",
                        color: "var(--text-primary)",
                        textAlign: isArabic ? "right" : "left",
                        fontWeight: "bold",
                        fontFamily: "Century Gothic",
                        marginTop: isMobile ? "1rem" : "4rem"
                    }}
                >
                    {qrText}
                </Typography>
            );
        }
        showTitle = true;
    } else if (pathToCheck === '/about-us') {
        // For about-us page, show the about9.png image
        sideImage = `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/about9.png`;
        showTitle = true;
    } else {
        // For other pages (home, blogs, etc.), show default contact form without custom text
        text = undefined;
        showTitle = true;
    }

    return (
        <ContactUs 
            isMobile={isMobile} 
            language={language} 
            translations={translations} 
            contactusRef={contactusRef}
            text={text}
            showTitle={showTitle}
            sideImage={sideImage}
        />
    );
}

